import * as React from "react";
import { format, parse, isValid, setMonth, setYear, addMonths } from "date-fns";
import { hr } from "date-fns/locale";
import { Calendar as CalendarIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Global styled DatePicker — API-compatible drop-in for `<input type="date" />`.
 *
 * Props:
 *  - value:       ISO date string "YYYY-MM-DD" or "" / null
 *  - onChange:    (iso: string) => void   // called with "YYYY-MM-DD" or ""
 *  - placeholder: string (default: "Odaberi datum")
 *  - className:   passes through to trigger button
 *  - disabled:    bool
 *  - "data-testid": string
 *  - clearable:   bool (default true) — show X clear button when value present
 *  - fromYear / toYear: number — restrict caption range (default 1900..2035)
 */
function isoToDate(v) {
  if (!v) return undefined;
  const d = parse(v, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

function dateToIso(d) {
  return d ? format(d, "yyyy-MM-dd") : "";
}

const MONTHS_HR = [
  "siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj",
  "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac",
];

/** Custom compact header with month/year dropdowns — replaces the ugly stacked
 *  captionLayout="dropdown-buttons" default. */
function CustomCaption({ displayMonth, fromYear, toYear, onMonthChange }) {
  const years = React.useMemo(() => {
    const arr = [];
    for (let y = toYear; y >= fromYear; y--) arr.push(y);
    return arr;
  }, [fromYear, toYear]);

  const handleMonth = (e) => onMonthChange(setMonth(displayMonth, Number(e.target.value)));
  const handleYear = (e) => onMonthChange(setYear(displayMonth, Number(e.target.value)));

  return (
    <div className="mb-2 flex items-center justify-between gap-2 px-1">
      <button
        type="button"
        aria-label="Prethodni mjesec"
        onClick={() => onMonthChange(addMonths(displayMonth, -1))}
        className={cn(buttonVariants({ variant: "outline" }), "h-7 w-7 shrink-0 p-0")}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <div className="flex flex-1 items-center justify-center gap-1.5">
        <select
          value={displayMonth.getMonth()}
          onChange={handleMonth}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {MONTHS_HR.map((m, i) => (
            <option key={m} value={i}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
          ))}
        </select>
        <select
          value={displayMonth.getFullYear()}
          onChange={handleYear}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        aria-label="Sljedeći mjesec"
        onClick={() => onMonthChange(addMonths(displayMonth, 1))}
        className={cn(buttonVariants({ variant: "outline" }), "h-7 w-7 shrink-0 p-0")}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Odaberi datum",
  className,
  disabled = false,
  clearable = true,
  fromYear = 1900,
  toYear = 2035,
  minDate,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const selected = isoToDate(value);
  const min = isoToDate(minDate);
  const [month, setMonthView] = React.useState(selected || min || new Date());
  const testId = rest["data-testid"];

  // Keep the visible month in sync when the caller changes `value` externally.
  React.useEffect(() => {
    if (selected) setMonthView(selected);
    else if (min) setMonthView(min);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minDate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between rounded-md border border-foreground/20 bg-card px-3 py-1 text-sm text-foreground shadow-sm transition-colors",
            "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">
              {selected ? format(selected, "dd.MM.yyyy") : placeholder}
            </span>
          </span>
          {clearable && selected && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Očisti datum"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.("");
              }}
              className="ml-2 rounded p-0.5 opacity-50 hover:bg-muted hover:opacity-100"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3 w-3" />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[200] w-auto p-3" align="start">
        <DayPicker
          mode="single"
          selected={selected}
          month={month}
          onMonthChange={setMonthView}
          disabled={min ? { before: min } : undefined}
          onSelect={(d) => {
            onChange?.(dateToIso(d));
            setOpen(false);
          }}
          locale={hr}
          weekStartsOn={1}
          showOutsideDays
          initialFocus
          components={{
            Caption: (p) => (
              <CustomCaption
                displayMonth={p.displayMonth}
                fromYear={fromYear}
                toYear={toYear}
                onMonthChange={setMonthView}
              />
            ),
          }}
          classNames={{
            months: "flex flex-col",
            month: "space-y-2",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "w-8 text-[10px] font-medium uppercase text-muted-foreground",
            row: "flex w-full mt-1",
            cell: "relative p-0 text-center text-sm",
            day: cn(
              buttonVariants({ variant: "ghost" }),
              "h-8 w-8 p-0 font-normal aria-selected:opacity-100",
            ),
            day_selected:
              "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
            day_today: "bg-accent text-accent-foreground",
            day_outside: "text-muted-foreground/50",
            day_disabled: "text-muted-foreground/40 opacity-50",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
